import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BlockIcon from '@mui/icons-material/Block'
import SendIcon from '@mui/icons-material/Send'
import KeyIcon from '@mui/icons-material/Key'
import { toast } from 'react-toastify'
import {
  fetchCertificatesForPatient,
  revokeCertificate,
  sendCertificateToPatient,
  updateCertificateAccess,
} from '../medicalCertificateService'
import { CERTIFICATE_STATUS, formatDisplayDate } from '../certificateUtils'

const STATUS_COLOR = {
  [CERTIFICATE_STATUS.ISSUED]: 'success',
  [CERTIFICATE_STATUS.DRAFT]: 'default',
  [CERTIFICATE_STATUS.REVOKED]: 'error',
}

/** View mode: lists a patient's certificates with open / revoke. */
export default function ExistingCertificatesList({ patientId, onCreateNew }) {
  const [loading, setLoading] = useState(true)
  const [certificates, setCertificates] = useState([])
  const [error, setError] = useState(null)
  const [revokingId, setRevokingId] = useState(null)
  // { open, cert, state: null|'queued'|'sent', sending }
  const [sendDialog, setSendDialog] = useState({ open: false, cert: null, state: null, sending: false })
  // { open, cert, policy, mobilesText, saving }
  const [accessDialog, setAccessDialog] = useState({
    open: false,
    cert: null,
    policy: 'ALLOW_ALL',
    mobilesText: '',
    saving: false,
  })

  const openAccessForCert = (cert) => {
    setAccessDialog({
      open: true,
      cert,
      policy: cert.viewAccessPolicy || 'ALLOW_ALL',
      mobilesText: (cert.allowedViewerMobiles || []).join('\n'),
      saving: false,
    })
  }

  const closeAccessDialog = () =>
    setAccessDialog({ open: false, cert: null, policy: 'ALLOW_ALL', mobilesText: '', saving: false })

  const handleSaveAccess = async () => {
    const { cert, policy, mobilesText } = accessDialog
    if (!cert) return
    const allowedViewerMobiles = mobilesText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    setAccessDialog((s) => ({ ...s, saving: true }))
    const res = await updateCertificateAccess(cert.id, { viewAccessPolicy: policy, allowedViewerMobiles })
    if (res.ok) {
      const updated = res.certificate
      if (updated) {
        setCertificates((list) => list.map((c) => (c.id === cert.id ? { ...c, ...updated } : c)))
      }
      toast.success('Access updated')
      closeAccessDialog()
    } else {
      setAccessDialog((s) => ({ ...s, saving: false }))
      toast.error(res.message || 'Failed to update access')
    }
  }

  const openSendForCert = async (cert) => {
    setSendDialog({ open: true, cert, state: null, sending: true })
    const res = await sendCertificateToPatient(cert.id, false)
    if (res.ok) {
      setSendDialog({ open: true, cert, state: 'queued', sending: false })
    } else {
      setSendDialog({ open: false, cert: null, state: null, sending: false })
      toast.error(res.message || 'Failed to queue certificate')
    }
  }

  const handleSendNow = async () => {
    const cert = sendDialog.cert
    if (!cert) return
    setSendDialog((s) => ({ ...s, sending: true }))
    const res = await sendCertificateToPatient(cert.id, true)
    if (res.ok) {
      setSendDialog((s) => ({ ...s, state: 'sent', sending: false }))
      toast.success('Certificate sent to the patient')
    } else {
      setSendDialog((s) => ({ ...s, sending: false }))
      toast.error(res.message || 'Failed to send now')
    }
  }

  const closeSendDialog = () => setSendDialog({ open: false, cert: null, state: null, sending: false })

  const load = useCallback(() => {
    if (!patientId) {
      setLoading(false)
      return
    }
    setLoading(true)
    fetchCertificatesForPatient(patientId)
      .then((res) => {
        if (res.ok) setCertificates(res.certificates)
        else setError(res.message)
      })
      .finally(() => setLoading(false))
  }, [patientId])

  useEffect(() => {
    load()
  }, [load])

  const handleOpen = (cert) => {
    if (cert?.pdfUrl) window.open(cert.pdfUrl, '_blank', 'noopener')
    else toast.error('PDF link not available')
  }

  const handleRevoke = async (cert) => {
    const reason = window.prompt('Reason for revoking this certificate?')
    if (reason === null) return
    setRevokingId(cert.id)
    const res = await revokeCertificate(cert.id, reason)
    setRevokingId(null)
    if (res.ok) {
      toast.success('Certificate revoked')
      load()
    } else {
      toast.error(res.message || 'Failed to revoke')
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  if (!certificates.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No certificates have been issued for this patient yet.
        </Typography>
        {onCreateNew && (
          <Button variant="contained" onClick={onCreateNew} sx={{ textTransform: 'none' }}>
            Create a certificate
          </Button>
        )}
      </Box>
    )
  }

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {certificates.map((cert, idx) => (
        <Box key={cert.id}>
          {idx > 0 && <Divider />}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
                {cert.certificateTitle || 'Medical document'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {formatDisplayDate(cert.issuedAt || cert.createdTime)}
                {cert.practitionerName ? ` • ${cert.practitionerName}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Chip
                size="small"
                label={cert.status || CERTIFICATE_STATUS.ISSUED}
                color={STATUS_COLOR[cert.status] || 'default'}
                variant="outlined"
              />
              <Tooltip title="Open PDF">
                <IconButton size="small" onClick={() => handleOpen(cert)}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {cert.status !== CERTIFICATE_STATUS.REVOKED && (
                <Tooltip title="Manage who can view">
                  <IconButton size="small" onClick={() => openAccessForCert(cert)}>
                    <KeyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {cert.status !== CERTIFICATE_STATUS.REVOKED && (
                <Tooltip title="Send to patient">
                  <IconButton size="small" color="secondary" onClick={() => openSendForCert(cert)}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {cert.status !== CERTIFICATE_STATUS.REVOKED && (
                <Tooltip title="Revoke">
                  <span>
                    <IconButton size="small" color="error" onClick={() => handleRevoke(cert)} disabled={revokingId === cert.id}>
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>

      <Dialog open={sendDialog.open} onClose={closeSendDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{sendDialog.state === 'sent' ? 'Sent to patient' : 'Queued for the patient'}</DialogTitle>
        <DialogContent>
          {sendDialog.sending && sendDialog.state === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <DialogContentText sx={{ fontSize: 14 }}>
              {sendDialog.state === 'sent'
                ? 'The certificate has been sent to the patient on their registered contact (email / WhatsApp).'
                : 'The certificate will be sent to the patient on their registered contact (email / WhatsApp) — usually within ~15 minutes during the day, or ~40 minutes at night. You can send it right away instead.'}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {sendDialog.state === 'queued' && (
            <Button onClick={handleSendNow} disabled={sendDialog.sending} startIcon={<SendIcon />} sx={{ textTransform: 'none' }}>
              Send now
            </Button>
          )}
          <Button variant="contained" onClick={closeSendDialog} sx={{ textTransform: 'none' }}>
            {sendDialog.state === 'sent' ? 'Done' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accessDialog.open} onClose={closeAccessDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Manage who can view</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Controls who can open this document via its verification link / QR code.
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={accessDialog.policy}
              onChange={(_e, v) => v && setAccessDialog((s) => ({ ...s, policy: v }))}
            >
              <ToggleButton value="OPEN" sx={{ textTransform: 'none', px: 1.5 }}>
                Open (no OTP)
              </ToggleButton>
              <ToggleButton value="ALLOW_ALL" sx={{ textTransform: 'none', px: 1.5 }}>
                Anyone (OTP)
              </ToggleButton>
              <ToggleButton value="WHITELIST" sx={{ textTransform: 'none', px: 1.5 }}>
                Restricted
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {accessDialog.policy === 'OPEN'
                ? 'Anyone who scans can view after entering their contact details (no OTP). The access is logged.'
                : accessDialog.policy === 'WHITELIST'
                  ? 'Only the listed numbers (and the patient) can view, after an OTP on WhatsApp.'
                  : 'Any mobile can view after an OTP on WhatsApp.'}
            </Typography>
            {accessDialog.policy === 'WHITELIST' && (
              <TextField
                label="Allowed mobile numbers (one per line)"
                value={accessDialog.mobilesText}
                onChange={(e) => setAccessDialog((s) => ({ ...s, mobilesText: e.target.value }))}
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder={'+91 98765 43210\n+91 91234 56789'}
                helperText="The patient's own number is always allowed. Add a number here to let that viewer in."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAccessDialog} disabled={accessDialog.saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAccess}
            disabled={accessDialog.saving}
            sx={{ textTransform: 'none' }}
          >
            {accessDialog.saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
